import { useEffect } from "react";
import { animate, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { CertWall } from "./CertWall";
import { Eyebrow, MaskLines, Reveal, useCoarse } from "./motion";

const Badge = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 90, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-16, 16]), { stiffness: 90, damping: 15 });
  const coarse = useCoarse();
  useEffect(() => {
    if (!coarse) return;
    const a = animate(mx, [0, 0.35, 0, -0.35, 0], { duration: 9, repeat: Infinity, ease: "easeInOut" });
    const b = animate(my, [0, -0.25, 0, 0.25, 0], { duration: 7, repeat: Infinity, ease: "easeInOut" });
    return () => { a.stop(); b.stop(); };
  }, [coarse, mx, my]);
  const onMove = (e) => {
    if (coarse) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div className="[perspective:1200px] flex justify-center" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="chamfer relative w-full max-w-[320px] sm:max-w-[380px] aspect-[3/4.2] bg-gradient-to-br from-[#121A28] to-[#0A0E16] border border-white/10 p-8 flex flex-col justify-between shadow-[0_40px_120px_-30px_rgba(0,240,255,0.25)]" data-testid="about-badge">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[0.3em] text-slate-500">LNS / ENGINEER</span>
          <span className="h-3 w-14 rounded-full bg-void border border-white/10" />
        </div>
        <div>
          <div className="grid h-24 w-24 place-items-center rounded-full border border-cyan-300/50 font-display text-4xl font-bold text-cyan-300">SR</div>
          <p className="mt-6 font-display text-3xl font-bold">Stephen Rae</p>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Founder · Network Engineer</p>
        </div>
        <div>
          <div className="flex flex-wrap gap-2">
            {["CCNP", "Palo Alto", "FortiGate", "Azure"].map((c) => (
              <span key={c} className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] text-slate-300">{c}</span>
            ))}
          </div>
          <div className="mt-6 flex items-end justify-between">
            <div className="flex gap-[3px] h-8 items-end">
              {Array.from({ length: 22 }).map((_, i) => <span key={i} className="w-[2px] bg-slate-500" style={{ height: `${30 + ((i * 37) % 70)}%` }} />)}
            </div>
            <span className="flex items-center gap-1.5 font-mono text-[10px] text-emerald-400"><ShieldCheck size={13} /> VERIFIED</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const About = () => (
  <section id="about" className="relative py-24 md:py-36" data-testid="about-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-16 items-center">
      <div className="lg:col-span-5"><Reveal><Badge /></Reveal></div>
      <div className="lg:col-span-7 min-w-0">
        <Eyebrow>About</Eyebrow>
        <h2 className="mt-6 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.02]">
          <MaskLines lines={["Built by an enterprise", <>engineer. <span className="font-serif-i font-normal text-cyan-300">Sized for you.</span></>]} />
        </h2>
        <Reveal delay={0.1}>
          <p className="mt-8 text-slate-400 leading-relaxed max-w-2xl">Lunar Network Solutions is run by Stephen Rae — a network engineer certified in Cisco Enterprise, Palo Alto, FortiGate and Azure cloud computing, with hands-on experience across Meraki and SD-WAN. Small businesses get networks designed to enterprise standards, delivered at a scale and price that makes sense.</p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-10 font-mono text-[10px] tracking-[0.25em] text-slate-500">CERTIFICATIONS · <span className="sm:hidden">SWIPE &amp; TAP TO FLIP</span><span className="hidden sm:inline">HOVER OR TAP TO FLIP</span></p>
          <CertWall />
        </Reveal>
      </div>
    </div>
  </section>
);
